/**
 * What one item looks like at each node's output — the shape the data picker
 * shows so a field can be clicked into an expression.
 *
 * Two sources, best first: the items a node produced in the last run (real
 * values, real keys), else a declared schema (a connector action's
 * `output_schema`, a callable's inputs, a webhook's request envelope). The
 * two are merged into one tree of fields with a sample value where known.
 */

import type { JsonSchema } from '@/core/types/api.types'
import type { WfEdge, WfNode } from '../types'

export interface FieldNode {
  /** Dotted path from the item root: `body.customer.email`, `lines.0.sku`. */
  path: string
  key: string
  type: string
  description?: string
  sample?: unknown
  children?: FieldNode[]
}

const MAX_DEPTH = 6
const MAX_KEYS = 80

// --- from a sample value ------------------------------------------------------------------

export function typeOf(value: unknown): string {
  if (value === null || value === undefined) return 'null'
  if (Array.isArray(value)) return 'array'
  return typeof value === 'object' ? 'object' : typeof value
}

/** A field tree from a real value. Arrays show their first element's shape. */
export function fieldsFromSample(value: unknown, base = '', depth = 0): FieldNode[] {
  if (depth > MAX_DEPTH) return []
  if (Array.isArray(value)) {
    const first = value[0]
    if (first === undefined) return []
    return [
      {
        path: base ? `${base}.0` : '0',
        key: '[0]',
        type: typeOf(first),
        sample: first,
        children: fieldsFromSample(first, base ? `${base}.0` : '0', depth + 1),
      },
    ]
  }
  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .slice(0, MAX_KEYS)
      .map(([k, v]) => ({
        path: base ? `${base}.${k}` : k,
        key: k,
        type: typeOf(v),
        sample: v,
        children: v && typeof v === 'object' ? fieldsFromSample(v, base ? `${base}.${k}` : k, depth + 1) : undefined,
      }))
  }
  return []
}

// --- from a JSON schema -------------------------------------------------------------------

function schemaType(s: JsonSchema | undefined): string {
  if (!s) return 'any'
  const t = Array.isArray(s.type) ? s.type.find((x) => x !== 'null') ?? s.type[0] : s.type
  if (t) return String(t)
  if (s.properties) return 'object'
  if (s.items) return 'array'
  return 'any'
}

/** A field tree from a declared schema. Arrays are shown through their item shape. */
export function fieldsFromSchema(schema: JsonSchema | undefined, base = '', depth = 0): FieldNode[] {
  if (!schema || depth > MAX_DEPTH) return []
  const props = schema.properties ?? {}
  return Object.entries(props)
    .slice(0, MAX_KEYS)
    .map(([k, s]) => {
      const path = base ? `${base}.${k}` : k
      const type = schemaType(s)
      let children: FieldNode[] | undefined
      if (type === 'object') children = fieldsFromSchema(s, path, depth + 1)
      else if (type === 'array' && s.items) {
        const itemType = schemaType(s.items)
        children = [
          {
            path: `${path}.0`,
            key: '[0]',
            type: itemType,
            children: itemType === 'object' ? fieldsFromSchema(s.items, `${path}.0`, depth + 1) : undefined,
          },
        ]
      }
      return { path, key: k, type, description: s.description ?? s['x-ui']?.help, children }
    })
}

/** The shape of one *item*: an array schema is unwrapped to its element. */
export function itemSchema(schema: JsonSchema | undefined): JsonSchema | undefined {
  if (!schema) return undefined
  if (schemaType(schema) === 'array') return schema.items
  return schema
}

/** Declared fields, decorated with sample values where the run had them. */
export function mergeFields(declared: FieldNode[], sampled: FieldNode[]): FieldNode[] {
  if (!declared.length) return sampled
  if (!sampled.length) return declared
  const byKey = new Map(sampled.map((f) => [f.key, f]))
  const out = declared.map((d) => {
    const s = byKey.get(d.key)
    if (!s) return d
    byKey.delete(d.key)
    return { ...d, sample: s.sample, type: d.type === 'any' ? s.type : d.type, children: mergeFields(d.children ?? [], s.children ?? []) }
  })
  // Keys the run produced that the schema did not mention are real too.
  return [...out, ...byKey.values()]
}

// --- the graph ----------------------------------------------------------------------------

/** Every node whose output can reach `nodeId`, nearest first. `direct` are its
    immediate predecessors — the ones `$json` means. */
export function upstreamOf(nodeId: string, nodes: WfNode[], edges: WfEdge[]): { direct: WfNode[]; all: WfNode[] } {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const parents = new Map<string, string[]>()
  for (const e of edges) parents.set(e.target, [...(parents.get(e.target) ?? []), e.source])
  const direct = (parents.get(nodeId) ?? []).map((id) => byId.get(id)).filter((n): n is WfNode => Boolean(n))
  const all: WfNode[] = []
  const seen = new Set<string>([nodeId])
  const queue = [...(parents.get(nodeId) ?? [])]
  while (queue.length) {
    const id = queue.shift()!
    if (seen.has(id)) continue
    seen.add(id)
    const n = byId.get(id)
    if (!n) continue
    all.push(n)
    queue.push(...(parents.get(id) ?? []))
  }
  return { direct, all }
}

// --- expressions --------------------------------------------------------------------------

const IDENT = /^[A-Za-z_][A-Za-z0-9_]*$/

/** `$json.a.b`, or `get($json, "a.b-c.0")` when a key is not a plain name. */
export function pathExpression(root: string, path: string): string {
  if (!path) return root
  const parts = path.split('.')
  if (parts.every((p) => IDENT.test(p) || /^\d+$/.test(p))) {
    return parts.reduce((acc, p) => (/^\d+$/.test(p) ? `${acc}[${p}]` : `${acc}.${p}`), root)
  }
  return `get(${root}, ${JSON.stringify(path)})`
}

/** The expression for a field of a source node, seen from the node being edited. */
export function referenceFor(source: WfNode, isDirect: boolean, path: string): string {
  const root = isDirect ? '$json' : `$node(${JSON.stringify(source.name)}).first`
  return `{{ ${pathExpression(root, path)} }}`
}

/** A short, readable preview of a sample value for the picker rows. */
export function previewOf(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value.length > 40 ? `${value.slice(0, 40)}…` : value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) return `[${value.length}]`
  if (typeof value === 'object') return `{${Object.keys(value as object).length}}`
  return String(value)
}
